/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import { Box } from '@mui/material';
import { PropsWithChildren } from 'react';
import dayjs from 'dayjs';
import QSTooltip from '../../../../../../shared/components/molecules/tooltip';
import TimeCircleIcon from '../../../../../../shared/icons/icons-revamp/time_circle';
import { QSTypography } from '../../../../../../shared/components/atoms';

const AVATAR_SIZE = 24;
const OVERLAP = 4;

export function getInitials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('');
}

type AvatarCircleProps = PropsWithChildren<{
  styles?: SerializedStyles;
}>;

export function AvatarCircle({ children, styles }: AvatarCircleProps) {
  return (
    <Box
      css={css(
        `
        width: ${AVATAR_SIZE}px;
        height: ${AVATAR_SIZE}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 10px;
        box-sizing: border-box;
        border: 1px solid #fff;
      `,
        styles
      )}
    >
      {children}
    </Box>
  );
}

export function AvatarStack({ users }: { users: string[] }) {
  if (!users.length) return null;

  if (users.length === 1) {
    return (
      <QSTooltip title={users[0]} arrow placement="top">
        <Box>
          <AvatarCircle
            styles={css`
              background: #f3ecf5;
              color: #ae5fc0;
            `}
          >
            {getInitials(users[0])}
          </AvatarCircle>
        </Box>
      </QSTooltip>
    );
  }

  const first = users[0];
  const count = users.length;

  const secondLabel = count === 2 ? getInitials(users[1]) : `+${count - 1}`;

  return (
    <QSTooltip title={users.join(', ')} arrow placement="top">
      <Box
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <AvatarCircle
          styles={css`
            margin-right: -${OVERLAP}px;
            z-index: 1;
            color: #317694;
            background: #31769426;
          `}
        >
          {getInitials(first)}
        </AvatarCircle>

        <AvatarCircle
          styles={css`
            z-index: 2;
            background: #f3ecf5;
            color: #ae5fc0;
          `}
        >
          {secondLabel}
        </AvatarCircle>
      </Box>
    </QSTooltip>
  );
}

interface ReminderDetailsProps {
  dueAt: string;
  assigneeDetails: {
    userId: string;
    name: string;
  }[];
  isReminderDismissed?: boolean;
}

export function ReminderDetails({ dueAt, assigneeDetails, isReminderDismissed }: ReminderDetailsProps) {
  const formattedDate = dayjs(dueAt).format('h:mm A, D MMM YYYY');

  return (
    <Box
      css={css`
        display: flex;
        align-items: center;
        gap: 4px;
      `}
    >
      <Box
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
        `}
      >
        <TimeCircleIcon size={18} />
        <QSTypography
          css={css`
            color: #7a7a7a;
            font-family: Inter;
            font-size: 14px;
            font-style: normal;
            font-weight: ${isReminderDismissed ? 400 : 500};
            line-height: normal;
          `}
        >
          {formattedDate}
        </QSTypography>
      </Box>
      <QSTypography
        css={css`
          color: #e0e0ea;
          font-family: Inter;
          font-size: 14px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        `}
      >
        {(assigneeDetails || []).length > 0 ? '•' : ''}
      </QSTypography>

      <AvatarStack users={(assigneeDetails || []).map(({ name }) => name)} />
    </Box>
  );
}
